import React, { useState, useEffect, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { api } from "../../../../services";
import { CONSTANTS } from "../../../../library/common/constant";
import { throttle } from "lodash";

const {
  DELETE_CONFIRM_TITLE,
  DELETE_BUTTON,
  CANCEL_BUTTON,
  SAVE_BUTTON,
  MANAGE_LOCATIONS,
  THROTTLE_DURATION,
} = CONSTANTS;

const LocationManager = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [currentType, setCurrentType] = useState("countries");
  const [currentItem, setCurrentItem] = useState({});
  const [formValues, setFormValues] = useState({});
  const [deleteDialogueOpen, setDeleteDialogueOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenDeleteDialogue = () => {
    setDeleteDialogueOpen(true);
  };

  const handleCloseDeleteDialogue = () => {
    setDeleteDialogueOpen(false);
  };

  const handleOpenDialouge = () => {
    setOpenDialog(true);
  };

  const handleCloseDialouge = () => {
    setOpenDialog(false);
  };

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await api.get("/countries");
      if (data?.countries) {
        setCountries(data.countries);
      }
    } catch (error) {
      console.error("Error fetching countries", error);
    }
  }, []);

  const fetchStates = useCallback(async () => {
    try {
      const { data } = await api.get("/states");
      const flattenedDistricts = data?.states?.flatMap((state) => {
        const temp = { ...state };
        delete state?.districts;
        return temp?.districts?.map((district) => ({
          _id: district?._id,
          name: { en: district?.name?.en, hi: district?.name?.hi },
          used: district?.used,
          state_nm: `${temp?.name?.en} - ${temp?.name?.hi}`,
          state: temp?._id,
        }));
      });
      setStates(data.states);
      setDistricts(flattenedDistricts);
    } catch (error) {
      console.error("Error fetching states", error);
    }
  }, []);

  const titles = ["Country", "State", "District"];

  useEffect(() => {
    fetchCountries();
    fetchStates();
  }, [fetchCountries, fetchStates]);

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
    const types = ["countries", "states", "districts"];
    setCurrentType(types[newIndex]);
    setFormValues({});
    setCurrentItem({});
    handleCloseDialouge();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormValues((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const openAddEditDialog = (mode, item = null) => {
    setDialogMode(mode);
    setCurrentItem(item || {});
    setFormValues(item ? { ...item, country: item?.country?._id } : {});
    handleOpenDialouge();
  };

  const handleSave = throttle(async () => {
    try {
      setIsLoading(true);
      const routes = ["/countries", "/states", "/states/district"];
      if (dialogMode === "add") {
        await api.post(routes[tabIndex], formValues);
        currentType === "countries" ? fetchCountries() : fetchStates();
      } else if (dialogMode === "edit") {
        const id = formValues._id;
        await api.patch(`${routes[tabIndex]}/${id}`, formValues);
        currentType === "countries" ? fetchCountries() : fetchStates();
      }
      handleCloseDialouge();
    } catch (error) {
      console.error("Save failed", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleDelete = throttle(async (item) => {
    try {
      setIsLoading(true);
      const id = item._id;
      const routes = ["/countries", "/states", "/states/district"];
      await api.delete(`${routes[tabIndex]}/${id}`);
      currentType === "countries" ? fetchCountries() : fetchStates();
      handleCloseDeleteDialogue();
    } catch (error) {
      console.error("Delete failed", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const getRowsData = () => {
    if (currentType === "countries") return countries;
    if (currentType === "states") return states;
    if (currentType === "districts") return districts;
    return [];
  };

  const getColumnsDefinition = () => {
    const columns = [
      {
        field: "name.en",
        headerName: "Name (En)",
        valueGetter: (value, row) => row?.name?.en,
        flex: 1,
      },
      {
        field: "name.hi",
        headerName: "Name (Hi)",
        valueGetter: (value, row) => row?.name?.hi,
        flex: 1,
      },
      ...(currentType === "districts"
        ? []
        : [{ field: "code", headerName: "Code", flex: 0.5 }]),
      ...(currentType === "states"
        ? [
            {
              field: "country",
              headerName: "Country",
              valueGetter: (value, row) =>
                `${row?.country?.name?.en || ""} - ${
                  row?.country?.name?.hi || ""
                }`,
              flex: 1,
            },
          ]
        : currentType === "districts"
        ? [
            {
              field: "state_nm",
              headerName: "State",
              flex: 1,
            },
          ]
        : []),
      {
        field: "used",
        headerName: "Is Used",
        valueGetter: (value, row) => (row?.used ? "Yes" : "No"),
        flex: 0.5,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        renderCell: (params) => (
          <>
            <IconButton onClick={() => openAddEditDialog("edit", params.row)}>
              <Edit />
            </IconButton>
            <IconButton
              onClick={() => {
                setCurrentItem(params.row);
                handleOpenDeleteDialogue();
              }}
              color="error"
            >
              <Delete />
            </IconButton>
          </>
        ),
      },
    ];
    return columns;
  };

  const getDialogBody = () => {
    return (
      <Stack spacing={2} padding={2}>
        <TextField
          fullWidth
          label="Name (En)"
          name="name.en"
          value={formValues?.name?.en || ""}
          onChange={handleFormChange}
        />
        <TextField
          fullWidth
          label="Name (Hi)"
          name="name.hi"
          value={formValues?.name?.hi || ""}
          onChange={handleFormChange}
        />
        {currentType !== "districts" && (
          <TextField
            fullWidth
            label="Code"
            name="code"
            value={formValues?.code || ""}
            onChange={handleFormChange}
          />
        )}
        {currentType !== "countries" && (
          <TextField
            fullWidth
            select
            label={titles[tabIndex - 1]}
            name={currentType === "states" ? "country" : "state"}
            value={
              formValues[currentType === "states" ? "country" : "state"] || ""
            }
            onChange={handleFormChange}
          >
            {currentType === "states"
              ? countries.map((country) => (
                  <MenuItem key={country._id} value={country._id}>
                    {country.name.en} - {country.name.hi}
                  </MenuItem>
                ))
              : states.map((state) => (
                  <MenuItem key={state._id} value={state._id}>
                    {state.name.en} - {state.name.hi}
                  </MenuItem>
                ))}
          </TextField>
        )}

        <TextField
          fullWidth
          select
          label="Is Used"
          name="used"
          value={formValues?.used?.toString() || "false"}
          onChange={(e) =>
            handleFormChange({
              target: {
                name: "used",
                value: e.target.value === "true",
              },
            })
          }
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </TextField>
      </Stack>
    );
  };

  return (
    <Paper sx={{ p: 2, m: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        {MANAGE_LOCATIONS || "Manage Locations"}
      </Typography>
      <Tabs value={tabIndex} onChange={handleTabChange} centered>
        <Tab label="Countries" />
        <Tab label="States" />
        <Tab label="Districts" />
      </Tabs>
      <Box sx={{ my: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={() => openAddEditDialog("add")}>
          Add New {titles[tabIndex]}
        </Button>
      </Box>
      <DataGrid
        rows={getRowsData()}
        columns={getColumnsDefinition()}
        getRowId={(row) => row?._id}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 10 } },
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableSelectionOnClick
        sx={{ border: 0 }}
        slots={{ toolbar: GridToolbar }}
      />
      <Dialog open={openDialog} onClose={handleCloseDialouge}>
        <DialogTitle>
          {dialogMode === "add"
            ? `Add New ${titles[tabIndex]}`
            : `Edit ${titles[tabIndex]}`}
        </DialogTitle>
        <DialogContent>{getDialogBody()}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialouge}>{CANCEL_BUTTON}</Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading}>
            {SAVE_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogueOpen} onClose={handleCloseDeleteDialogue}>
        <DialogTitle>{DELETE_CONFIRM_TITLE}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            {currentItem ? currentItem.name?.en || currentItem?.name || "" : ""}
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialogue}>{CANCEL_BUTTON}</Button>
          <Button
            onClick={() => {
              if (currentItem) {
                handleDelete(currentItem);
              }
            }}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {DELETE_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LocationManager;
